import React, { Fragment, ReactElement, ReactNode, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Portal, Transition } from '@headlessui/react';
import { generateTestId } from '../../utils/generateTestId';

export interface AutoCompleteBaseProps extends React.InputHTMLAttributes<HTMLInputElement> {
  dataTestId?: string;
  error?: boolean;
  StartAddon?: ReactNode;
  EndAddon?: ReactNode;
  className?: string;
  children?: ReactElement | null;
}

export interface AutoCompleteMultipleProps<Tag> extends AutoCompleteBaseProps {
  multiple: true;
  tags: Tag[];
  renderTag: (_tag: Tag, _index: number) => ReactNode;
}

export interface AutoCompleteProps extends AutoCompleteBaseProps {
  multiple?: false;
}

type AutoCompleteType = <Tag>(
  _props: (AutoCompleteProps | AutoCompleteMultipleProps<Tag>) & {
    ref?: React.Ref<HTMLInputElement>;
  },
) => JSX.Element;

const AutoComplete = <Tag,>(
  props: AutoCompleteProps | AutoCompleteMultipleProps<Tag>,
  ref: React.Ref<HTMLInputElement>,
) => {
  const { className, StartAddon, EndAddon, error, children, dataTestId, ...rest } = props;
  const testIds = generateTestId(dataTestId || 'autocomplete');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverStyles, setPopoverStyles] = useState({});

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (children) {
      setPopoverVisible(true);
      updatePopoverPosition();
    }
    rest.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Deixa um pequeno atraso para permitir cliques nos itens do popover
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setPopoverVisible(false);
      }
    }, 100);
    rest.onBlur?.(e);
  };

  const updatePopoverPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPopoverStyles({
        position: 'absolute',
        top: `${rect.bottom + 8}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 1000,
      });
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        data-testid={testIds.component}
        className={clsx(
          className,
          'p-2 gap-2 flex items-center flex-wrap border rounded-md bg-white border-gray-200 overflow-hidden',
          'transition-all focus-within:border-primary-500 focus-within:shadow-[inset_0px_0px_0px_2px] focus-within:shadow-primary-200',
          {
            'text-error-600 border-error-500 focus-within:border-error-500 focus-within:shadow-error-200': error,
          },
        )}
      >
        {StartAddon && <span className='flex-shrink-0'>{StartAddon}</span>}

        {'multiple' in props && props.multiple ? props.tags.map(props.renderTag) : null}

        <input
          {...rest}
          ref={el => {
            inputRef.current = el;
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          data-testid={testIds.input}
          className='flex-1 bg-transparent p-0.5 text-sm border-none focus:ring-0 disabled:opacity-40 focus:outline-none'
        />

        {EndAddon && <span className='flex-shrink-0'>{EndAddon}</span>}
      </div>

      {children && (
        <Portal>
          <Transition
            show={popoverVisible}
            as={Fragment}
            enter='ease-out duration-200'
            enterFrom='opacity-0 translate-y-1'
            enterTo='opacity-100 translate-y-0'
            leave='ease-in duration-150'
            leaveFrom='opacity-100 translate-y-0'
            leaveTo='opacity-0 translate-y-1'
          >
            <div
              style={popoverStyles}
              className='bg-white shadow-lg rounded-lg overflow-hidden max-h-64 overflow-y-auto'
              data-testid={testIds.popover}
            >
              {children}
            </div>
          </Transition>
        </Portal>
      )}
    </>
  );
};

// Usando forwardRef para passar a referência do input
const ForwardedAutoComplete = React.forwardRef(AutoComplete) as unknown as AutoCompleteType;

// Definir o displayName
Object.defineProperty(ForwardedAutoComplete, 'displayName', {
  value: 'AutoComplete',
});

export default ForwardedAutoComplete;
